import Archives from '@polleverywhere/core/lib/models/archive_collection'
import URI from 'urijs'
import { pluralize } from '@polleverywhere/core/lib/util'
import GraphQLPoll from '../graphQLPoll'
import { peFetch, apiUrlFor } from '@polleverywhere/fetch'

/**
 * Perform some operations whenever activity is re-hydrated.
 * @param {Backbone.Model|Collection} modelOrCollection
 * @param {Object} response
 * @param {Object} options Options passed into `save|fetch|etc`
 */
function handleActivitySync (modelOrCollection, response, options = {}) {
  // For MCPs, `parse` accepts a `resetOptions` flag to skip parsing options
  // data. Only when this flag is explicitly `false` do we know options data
  // is being skipped. Thus, only clear current archive otherwise.
  if (options.resetOptions !== false) {
    this._currentArchive = undefined
  }
}

/**
 * Handle whenever archive results data is fetched.
 * @param {Backbone.Model.Archive|Collection.Archives} model
 * @param {Object} data Response data
 * @param {Object} options When `silent` is true, do nothing.
 */
function handleArchiveData (model, data, options = {}) {
  // When syncing individual archives inside the collection, passing silent does
  // not suppress the sync event from triggering for some reason. So do it here.
  if (options.silent) { return }

  // Ignore fetches occurring against the collection. We only need to parse data
  // when an individual archived result is fetched.
  if (model instanceof Archives) { return }

  const pollData = {}
  if (model.has('options')) {
    // For MCPs
    pollData.options = model.get('options')
  }
  if (model.has('results')) {
    // TODO: remove this when `clicks` is deprecated from the normalizer in instance_common.js #166968428
    const key = this.activity.type() === 'clickable_image' ? 'clicks' : 'results'
    pollData[key] = model.get('results')
  }

  this.parse(pollData, options)

  // Keep record of which archive is currently loaded.
  this._currentArchive = model
}

export default class PresenterPoll extends GraphQLPoll {
  /**
   * Read-only collection of archived results. Consumers can use this property
   * to fetch a list of archives and/or an individual archived result.
   * @type {Backbone.Collection}
   */
  get archives () {
    if (!this._archives || this._archives.activity !== this.activity) {
      this._archives = new Archives([], { activity: this.activity })

      // Update activity results whenever fetching a single archive.
      this.listenTo(this._archives, 'sync', handleArchiveData)

      // To keep track of current archive, track when activity is re-hydrated.
      this.listenTo(this, 'sync', handleActivitySync)
    }

    return this._archives
  }

  /**
   * Read-only property consumers can use to determine if this activity is
   * loaded with an archived result. Returns `null` when using latest.
   * @return {Archive}
   */
  get currentArchive () {
    return this._currentArchive
  }

  isMQA () {
    return false
  }

  mqaType () {
    if (this.has('competition_id')) {
      return 'competition'
    } else if (this.has('survey_id')) {
      return 'survey'
    }
  }

  permalink () {
    return this.get('permalink')
  }

  isGraded () {
    return false
  }

  start (opts) {
    this.save({ state: 'opened' }, { patch: true })
  }

  stop (opts) {
    this.save({ state: 'closed' }, { patch: true })
  }

  activate () {
    const body = new URLSearchParams({
      type: this.type(),
      uid: this.get('shared_key')
    })
    if (this.presenter.get('id')) {
      body.set('user_id', this.presenter.get('id'))
    }
    return peFetch(apiUrlFor('/api/activation'), { method: 'PUT', body }).then(() => {
      this.set({ activated: true, current: true })
    })
  }

  deactivate () {
    const body = new URLSearchParams()
    if (this.presenter.get('id')) {
      body.set('user_id', this.presenter.get('id'))
    }
    return peFetch(apiUrlFor('/api/activation'), { method: 'DELETE', body }).then(() => {
      this.set({ activated: false, current: false })
    })
  }

  /**
   * Public shareable response link. Should support both cases when poll has
   * only the permalink or a participant_key.
   * @return {string}
   */
  webVotingUrl () {
    const type = pluralize(2, this.type())
    const key = this.get('participant_key')
    if (!key) {
      throw new Error(`${this.type}: webRespondUrl requires a participant_key`)
    }
    return URI('/')
      .segment(type)
      .segment(key)
      .segment('respond')
      .toString()
  }

  /*
   * A URL a user would visit in a browser, as opposed to @url which is just
   * a JSON endpoint.
   */
  showUrl () {
    return this.#urlBase().toString()
  }

  editUrl () {
    return this.#urlBase().segment('edit').toString()
  }

  duplicateUrl () {
    return this.#urlBase().segment('duplicate').toString()
  }

  /**
   * Generates url to old API endpoints (non-instance) via permalink.
   */
  #urlBase () {
    const type = pluralize(2, this.type())
    const permalink = this.get('permalink') || this.get('shared_key')

    return URI('/').segment(type).segment(permalink)
  }
}
